import React, { useState } from "react";
import {
  Select,
  MenuItem,
  Box,
  IconButton,
  Checkbox,
  ListItemText,
  OutlinedInput,
} from "@mui/material";
import { X } from "lucide-react";
import { inputStyle, selectStyle } from "./ChartBuilder";

const PERIOD_PRESETS = [
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "wtd", label: "Week to Date" },
  { value: "mtd", label: "Month to Date" },
  { value: "qtd", label: "Quarter to Date" },
  { value: "ytd", label: "Year to Date" },
  { value: "prev_week", label: "Previous Week" },
  { value: "prev_month", label: "Previous Month" },
  { value: "prev_quarter", label: "Previous Quarter" },
  { value: "prev_year", label: "Previous Year" },
];

export const OPERATOR_LABELS = {
  eq: "=",
  ne: "≠",
  gt: ">",
  lt: "<",
  gte: "≥",
  lte: "≤",
  contains: "contains",
  in: "in",
  between: "between",
  preset: "preset",
  nil: "is null",
  not_nil: "is not null",
};

const defaultValueForType = (type) => {
  if (type === "datetime" || type === "date") return "";
  if (type === "boolean") return "true";
  if (type === "integer" || type === "decimal") return "";
  return "";
};

const FilterRow = ({ filter, fields, onChange, onRemove }) => {
  const fieldMeta = fields.find((f) => f.name === filter.field);
  const operators = fieldMeta?.operators || [];
  const type = fieldMeta?.type;

  const [inDraft, setInDraft] = useState(() =>
    filter.operator === "in" && Array.isArray(filter.value) ? filter.value.join(", ") : ""
  );

  React.useEffect(() => {
    if (filter.operator === "in" && Array.isArray(filter.value)) {
      setInDraft(filter.value.join(", "));
    }
  }, [filter.operator]);

  const updateField = (newField) => {
    const meta = fields.find((f) => f.name === newField);
    const op = meta?.operators?.[0] || "eq";
    onChange({ field: newField, operator: op, value: defaultValueForType(meta?.type) });
  };

  const updateOp = (op) => {
    let value = filter.value;
    if (op === "in") value = Array.isArray(filter.value) ? filter.value : [];
    else if (op === "between") value = Array.isArray(filter.value) && filter.value.length === 2 ? filter.value : ["", ""];
    else if (Array.isArray(filter.value)) value = filter.value[0] ?? "";
    onChange({ ...filter, operator: op, value });
  };

  const renderValueInput = () => {
    if (!filter.field) return null;
    if (filter.operator === "nil" || filter.operator === "not_nil") return null;

    if (filter.operator === "in" && fieldMeta?.options) {
      const arr = Array.isArray(filter.value) ? filter.value : [];
      return (
        <Select
          multiple
          displayEmpty
          value={arr}
          onChange={(e) => onChange({ ...filter, value: e.target.value })}
          input={<OutlinedInput sx={selectStyle} />}
          renderValue={(selected) => (selected.length ? selected.join(", ") : "Select…")}
          fullWidth
        >
          {fieldMeta.options.map((opt) => (
            <MenuItem key={opt} value={opt} sx={{ fontSize: 13 }}>
              <Checkbox size="small" checked={arr.indexOf(opt) > -1} />
              <ListItemText primary={opt} primaryTypographyProps={{ fontSize: 13 }} />
            </MenuItem>
          ))}
        </Select>
      );
    }

    if (filter.operator === "in") {
      return (
        <Box
          component="input"
          placeholder="value1, value2, value3"
          value={inDraft}
          onChange={(e) => {
            const raw = e.target.value;
            setInDraft(raw);
            if (!raw.trimEnd().endsWith(",")) {
              const arr = raw.split(",").map((v) => v.trim()).filter(Boolean);
              onChange({ ...filter, value: arr });
            }
          }}
          onBlur={() => {
            const arr = inDraft.split(",").map((v) => v.trim()).filter(Boolean);
            onChange({ ...filter, value: arr });
            setInDraft(arr.join(", "));
          }}
          sx={{ ...inputStyle, width: "100%" }}
        />
      );
    }

    if (filter.operator === "preset" && (type === "datetime" || type === "date")) {
      return (
        <Select
          value={filter.value || ""}
          onChange={(e) => onChange({ ...filter, value: e.target.value })}
          sx={{ ...selectStyle, width: "100%" }}
          displayEmpty
        >
          <MenuItem value="" disabled>Select period...</MenuItem>
          {PERIOD_PRESETS.map((preset) => (
            <MenuItem key={preset.value} value={preset.value} sx={{ fontSize: 13 }}>
              {preset.label}
            </MenuItem>
          ))}
        </Select>
      );
    }

    if (filter.operator === "between") {
      const [a, b] = Array.isArray(filter.value) ? filter.value : ["", ""];
      const inputType = type === "datetime" || type === "date" ? "date" : "number";
      return (
        <Box sx={{ display: "flex", gap: 0.5, width: "100%" }}>
          <Box
            component="input"
            type={inputType}
            value={a || ""}
            onChange={(e) => onChange({ ...filter, value: [e.target.value, b] })}
            sx={{ ...inputStyle, flex: 1 }}
          />
          <Box
            component="input"
            type={inputType}
            value={b || ""}
            onChange={(e) => onChange({ ...filter, value: [a, e.target.value] })}
            sx={{ ...inputStyle, flex: 1 }}
          />
        </Box>
      );
    }

    if (type === "enum" && fieldMeta?.options) {
      return (
        <Select
          value={filter.value || ""}
          onChange={(e) => onChange({ ...filter, value: e.target.value })}
          sx={{ ...selectStyle, width: "100%" }}
          displayEmpty
        >
          <MenuItem value="" sx={{ fontSize: 13, color: "hsl(150 10% 46%)" }}>
            Select…
          </MenuItem>
          {fieldMeta.options.map((opt) => (
            <MenuItem key={opt} value={opt} sx={{ fontSize: 13 }}>
              {opt}
            </MenuItem>
          ))}
        </Select>
      );
    }

    if (type === "boolean") {
      return (
        <Select
          value={filter.value ?? "true"}
          onChange={(e) => onChange({ ...filter, value: e.target.value })}
          sx={{ ...selectStyle, width: "100%" }}
        >
          <MenuItem value="true" sx={{ fontSize: 13 }}>True</MenuItem>
          <MenuItem value="false" sx={{ fontSize: 13 }}>False</MenuItem>
        </Select>
      );
    }

    if (type === "datetime" || type === "date") {
      return (
        <Box
          component="input"
          type="date"
          value={filter.value || ""}
          onChange={(e) => onChange({ ...filter, value: e.target.value })}
          sx={{ ...inputStyle, width: "100%" }}
        />
      );
    }

    if (type === "integer" || type === "decimal") {
      return (
        <Box
          component="input"
          type="number"
          value={filter.value || ""}
          onChange={(e) => onChange({ ...filter, value: e.target.value })}
          sx={{ ...inputStyle, width: "100%" }}
        />
      );
    }

    return (
      <Box
        component="input"
        type="text"
        value={filter.value || ""}
        onChange={(e) => onChange({ ...filter, value: e.target.value })}
        sx={{ ...inputStyle, width: "100%" }}
      />
    );
  };

  const valueInput = renderValueInput();

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
      <Box sx={{ display: "flex", gap: 0.75, alignItems: "center" }}>
        <Select
          value={filter.field || ""}
          onChange={(e) => updateField(e.target.value)}
          sx={{ ...selectStyle, flex: 1.2 }}
          displayEmpty
        >
          <MenuItem value="" sx={{ fontSize: 13, color: "hsl(150 10% 46%)" }}>Field…</MenuItem>
          {fields.map((f) => (
            <MenuItem key={f.name} value={f.name} sx={{ fontSize: 13 }}>
              {f.label}
            </MenuItem>
          ))}
        </Select>
        <Select
          value={filter.operator || ""}
          onChange={(e) => updateOp(e.target.value)}
          sx={{ ...selectStyle, width: 80 }}
          disabled={!filter.field}
        >
          {operators.map((op) => (
            <MenuItem key={op} value={op} sx={{ fontSize: 13 }}>
              {OPERATOR_LABELS[op] || op}
            </MenuItem>
          ))}
        </Select>
        <IconButton size="small" onClick={onRemove} sx={{ color: "hsl(150 10% 46%)", flexShrink: 0 }}>
          <X size={14} />
        </IconButton>
      </Box>
      {valueInput && (
        <Box>{valueInput}</Box>
      )}
    </Box>
  );
};

export default FilterRow;
